import React, { useEffect, useRef, useState } from "react";
import {
  Mic,
  MicOff,
  Search as SearchIcon,
  Phone,
  MapPin,
  UserRound,
  Briefcase,
  Store,
  Package,
  X
} from "lucide-react";

import API from "../../api";
import "./Search.css";

export default function Search({
  user,
  navigate,
  onConsult
}) {
  const [searchText, setSearchText] = useState("");

  const [searchResults, setSearchResults] = useState({
    professionals: [],
    vendors: [],
    products: [],
    workPosts: []
  });

  const [isSearching, setIsSearching] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const recognitionRef = useRef(null);


  /* =====================================================
     SEARCH
  ===================================================== */

  const handleSearchWithText = async (text) => {
    const value = text.trim();

    if (!value) {
      setIsSearching(false);

      setSearchResults({
        professionals: [],
        vendors: [],
        products: [],
        workPosts: []
      });

      return;
    }

    setIsSearching(true);

    try {
      const res = await API.get(
        `/search?q=${encodeURIComponent(value)}`
      );

      if (res.data.success) {

        setSearchResults(
          res.data.results || {
            professionals: [],
            vendors: [],
            products: [],
            workPosts: []
          }
        );

      } else {

        setSearchResults({
          professionals: [],
          vendors: [],
          products: [],
          workPosts: []
        });

      }

    } catch (err) {

      console.error(
        "Universal search failed:",
        err
      );

      setSearchResults({
        professionals: [],
        vendors: [],
        products: [],
        workPosts: []
      });
    }
  };


  const handleSearch = () => {
    handleSearchWithText(searchText);
  };


  /* =====================================================
     VOICE SEARCH
  ===================================================== */

  const handleVoiceSearch = () => {

    const SpeechRecognition =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert(
        "Voice search is not supported in this browser."
      );
      return;
    }

    if (
      isListening &&
      recognitionRef.current
    ) {
      recognitionRef.current.stop();
      return;
    }

    const recognition =
      new SpeechRecognition();

    recognition.lang = "en-IN";
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {

      const transcript =
        event.results?.[0]?.[0]?.transcript?.trim();

      if (!transcript) return;

      setSearchText(transcript);

      handleSearchWithText(transcript);
    };

    recognition.onerror = (event) => {

      console.error(
        "Voice search error:",
        event.error
      );

      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current =
      recognition;

    recognition.start();
  };


  useEffect(() => {

    return () => {

      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }

    };

  }, []);


  /* =====================================================
     RESULT COUNTS
  ===================================================== */

  const professionals =
    searchResults.professionals || [];

  const vendors =
    searchResults.vendors || [];

  const products =
    searchResults.products || [];

  const workPosts =
    searchResults.workPosts || [];

  const totalResults =
    professionals.length +
    vendors.length +
    products.length +
    workPosts.length;


  /* =====================================================
     RENDER
  ===================================================== */

 return (
  <>
    <div className="home-search-wrapper">

      <SearchIcon
        size={18}
        className="home-search-icon"
      />

      <input
        className="home-search"
        placeholder="Search professionals, materials, vendors..."
        value={searchText}
        onChange={(e) => {
          const value = e.target.value;

          setSearchText(value);

          if (!value.trim()) {
            setIsSearching(false);
            setSearchResults({
              professionals: [],
              vendors: [],
              products: [],
              workPosts: []
            });
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSearch();
          }
        }}
      />

      <button
        type="button"
        className={`home-voice-search ${
          isListening ? "listening" : ""
        }`}
        onClick={handleVoiceSearch}
        aria-label={
          isListening
            ? "Stop voice search"
            : "Start voice search"
        }
      >
        {isListening ? (
          <MicOff size={19} />
        ) : (
          <Mic size={19} />
        )}
      </button>

      {/* SEARCH RESULTS */}
      {isSearching && (
        <div className="civilink-search-dropdown">

          {/* HEADER */}
          <div className="civilink-search-dropdown-header">
            <div>
              <span className="civilink-search-label">
                Search results
              </span>

              <h3>
                {searchText}
              </h3>
            </div>

            <div className="civilink-search-header-actions">

  <span className="civilink-search-count">
    {totalResults}
  </span>

  <button
    type="button"
    className="civilink-search-close"
    onClick={() => {
      setIsSearching(false);
    }}
    aria-label="Close search results"
  >
    <X size={18} />
  </button>

</div>
          </div>

          {/* NO RESULTS */}
          {totalResults === 0 && (
            <div className="civilink-search-empty">
              <div className="civilink-search-empty-icon">
                <SearchIcon size={22} />
              </div>

              <h3>No results found</h3>

              <p>
                Try a professional, material, shop or work.
              </p>
            </div>
          )}

          {/* ================= PRODUCTS ================= */}
          {products.length > 0 && (
            <div className="civilink-search-section">

              <div className="civilink-search-section-title">
                <div className="civilink-search-section-icon product">
                  <Package size={16} />
                </div>

                <span>Materials & Products</span>

                <small>{products.length}</small>
              </div>

              <div className="civilink-search-items">

                {products.slice(0, 3).map((product) => (
                 <div
  key={product._id}
  className="civilink-search-item"
  onClick={() => {
  console.log("SEARCH PRODUCT CLICKED:", product);

  if (!product.vendorProductCode) {
    console.error(
      "vendorProductCode missing from search result:",
      product
    );
    return;
  }

  setIsSearching(false);

  navigate(
    `/product/${encodeURIComponent(product.vendorProductCode)}`
  );
}}
>
                    <div className="civilink-search-item-image">

                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt=""
                        />
                      ) : (
                        <Package size={20} />
                      )}

                    </div>

                    <div className="civilink-search-item-info">
                      <strong>
                        {product.name}
                      </strong>

                      <span>
                        {product.subCategory ||
                          product.category ||
                          "Construction material"}
                      </span>

                      {product.vendor?.name && (
                        <small>
                          {product.vendor.name}
                        </small>
                      )}
                    </div>

                    <div className="civilink-search-arrow">
                      →
                    </div>
                  </div>
                ))}

              </div>
            </div>
          )}

          {/* ================= VENDORS ================= */}
          {vendors.length > 0 && (
            <div className="civilink-search-section">

              <div className="civilink-search-section-title">
                <div className="civilink-search-section-icon vendor">
                  <Store size={16} />
                </div>

                <span>Shops & Vendors</span>

                <small>{vendors.length}</small>
              </div>

              <div className="civilink-search-items">

                {vendors.slice(0, 3).map((vendor) => (
                  <div
                    key={vendor._id}
                    className="civilink-search-item"
                  >
                    <div className="civilink-search-item-image">

                      {vendor.image ? (
                        <img
                          src={vendor.image}
                          alt=""
                        />
                      ) : (
                        <Store size={20} />
                      )}

                    </div>

                    <div className="civilink-search-item-info">
                      <strong>
                        {vendor.name}
                      </strong>

                      <span>
                        Construction vendor
                      </span>

                      {vendor.location?.city && (
                        <small>
                          <MapPin size={12} />
                          {vendor.location.city}
                          {vendor.location.state
                            ? `, ${vendor.location.state}`
                            : ""}
                        </small>
                      )}
                    </div>

                    <div className="civilink-search-arrow">
                      →
                    </div>
                  </div>
                ))}

              </div>
            </div>
          )}

          {/* ================= PROFESSIONALS ================= */}
          {professionals.length > 0 && (
            <div className="civilink-search-section">

              <div className="civilink-search-section-title">
                <div className="civilink-search-section-icon person">
                  <UserRound size={16} />
                </div>

                <span>People</span>

                <small>{professionals.length}</small>
              </div>

              <div className="civilink-search-items">

                {professionals.slice(0, 3).map((person) => (
                  <div
                    key={person._id}
                    className="civilink-search-item civilink-search-person"
                    onClick={() =>
                      navigate(`/profile/${person._id}`)
                    }
                  >

                    <div className="civilink-search-item-image">

                      {person.profilePhoto ? (
                        <img
                          src={person.profilePhoto}
                          alt=""
                        />
                      ) : (
                        <UserRound size={20} />
                      )}

                    </div>

                    <div className="civilink-search-item-info">

                      <strong>
                        {person.name}
                      </strong>

                      <span>
                        {person.profession ||
                          "Construction professional"}
                      </span>

                      {person.location?.city && (
                        <small>
                          <MapPin size={12} />
                          {person.location.city}
                          {person.location.state
                            ? `, ${person.location.state}`
                            : ""}
                        </small>
                      )}

                    </div>

                    <button
                      className="civilink-search-consult"
                      onClick={(e) => {
                        e.stopPropagation();

                        if (
                          !localStorage.getItem("token")
                        ) {
                          navigate("/");
                          return;
                        }

                        onConsult?.(person);
                      }}
                    >
                      <Phone size={14} />
                      Consult
                    </button>

                  </div>
                ))}

              </div>
            </div>
          )}

          {/* ================= WORK ================= */}
          {workPosts.length > 0 && (
            <div className="civilink-search-section">

              <div className="civilink-search-section-title">
                <div className="civilink-search-section-icon work">
                  <Briefcase size={16} />
                </div>

                <span>Work</span>

                <small>{workPosts.length}</small>
              </div>

              <div className="civilink-search-items">

                {workPosts.slice(0, 3).map((work) => (
                  <div
                    key={work._id}
                    className="civilink-search-item"
                  >

                    <div className="civilink-search-item-image">
                      <Briefcase size={20} />
                    </div>

                    <div className="civilink-search-item-info">

                      <strong>
                        {work.title}
                      </strong>

                      <span>
                        {work.workType ||
                          "Construction work"}
                      </span>

                      {work.location?.city && (
                        <small>
                          <MapPin size={12} />
                          {work.location.city}
                          {work.location.state
                            ? `, ${work.location.state}`
                            : ""}
                        </small>
                      )}

                    </div>

                    <div className="civilink-search-arrow">
                      →
                    </div>

                  </div>
                ))}

              </div>
            </div>
          )}

          {/* FOOTER */}
          {totalResults > 12 && (
            <div className="civilink-search-footer">
              <span>
                Showing the most relevant results
              </span>

              <button type="button">
                See all results →
              </button>
            </div>
          )}

        </div>
      )}

    </div>
  </>
);
}